'use client';

import { useEffect, useMemo, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';

import { resolveDefaultVault } from '@core/be/auth/AuthConfig';
import { useAppReadyOptional } from '@core/helpers/react/contexts/AppReadyContext';

const AUTH_CHANNEL = 'inovisum-auth';
const AUTH_LAST_URL_KEY = 'auth:last-url';
const AUTH_REDIRECT_FLAG = 'auth:redirect-pending';
const AUTH_PATHS = new Set(['/auth/signin', '/auth/complete', '/auth/callback', '/auth/error', '/auth/not-allowed']);

const createInstanceId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

const isSafeRelativeUrl = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.startsWith('/auth')) return false;

  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};

export function AuthEffects() {
  const { status, data, update } = useSession();
  const router = useRouter();
  const pathname = usePathname() ?? '';
  const appReady = useAppReadyOptional();

  const instanceId = useMemo(createInstanceId, []);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const suppressBroadcastRef = useRef(false);
  const previousStatusRef = useRef(status);

  // Signal 'auth' ready when session status resolves (authenticated or unauthenticated)
  useEffect(() => {
    if (status !== 'loading') {
      appReady?.signalReady('auth');
    }
  }, [status, appReady]);

  // Persist the last safe URL as the user navigates
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!pathname) return;
    if (AUTH_PATHS.has(pathname)) return;

    try {
      const search = window.location.search ?? '';
      window.sessionStorage?.setItem(AUTH_LAST_URL_KEY, `${pathname}${search}`);
    } catch {}
  }, [pathname]);

  // Broadcast auth changes to other tabs
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channelRef.current = channel;

    const handleMessage = (event: MessageEvent) => {
      try {
        const payload = event.data;
        if (!payload || typeof payload !== 'object') return;
        if (payload.sourceId === instanceId) return;
        if (payload.type !== 'auth:state-change') return;

        suppressBroadcastRef.current = true;
        // Schedule session update outside the message handler to avoid long handler warnings
        setTimeout(() => {
          update().catch((error) => {
            console.warn('Failed to refresh session after auth broadcast', error);
          });
        }, 0);
      } catch (error) {
        // Ignore extension-related message handling errors
        if (error instanceof Error && error.message.includes('message channel closed')) {
          return;
        }
        console.warn('BroadcastChannel message handling error:', error);
      }
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [instanceId, update]);

  // Emit events when local auth state changes
  useEffect(() => {
    if (!channelRef.current) return;

    if (previousStatusRef.current === status) return;

    if (suppressBroadcastRef.current) {
      previousStatusRef.current = status;

      if (status !== 'loading') {
        suppressBroadcastRef.current = false;
      }

      return;
    }

    if (status === 'authenticated' || status === 'unauthenticated') {
      channelRef.current.postMessage({
        type: 'auth:state-change',
        status,
        userId: data?.user?.id ?? null,
        ts: Date.now(),
        sourceId: instanceId,
      });
    }

    previousStatusRef.current = status;
  }, [status, data?.user?.id, instanceId]);

  // Apply redirect policy after authentication completes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (status !== 'authenticated') {
      if (status === 'unauthenticated') {
        window.sessionStorage?.removeItem(AUTH_REDIRECT_FLAG);
        window.sessionStorage?.removeItem(AUTH_LAST_URL_KEY);
      }
      return;
    }

    const sessionLastUrl = window.sessionStorage?.getItem(AUTH_LAST_URL_KEY) ?? null;
    const redirectPending = window.sessionStorage?.getItem(AUTH_REDIRECT_FLAG);
    const onAuthPage = pathname ? AUTH_PATHS.has(pathname) : false;

    if (!redirectPending && !onAuthPage) {
      return;
    }

    let target: string | null = null;

    if (sessionLastUrl && isSafeRelativeUrl(sessionLastUrl)) {
      target = sessionLastUrl;
    }

    if (!target) {
      const defaultVault = resolveDefaultVault(data?.user?.vaultIds);
      if (defaultVault) {
        target = `/${defaultVault}`;
      }
    }

    if (!target) {
      target = '/no-access';
    }

    const current = `${window.location.pathname}${window.location.search}`;
    if (target !== current) {
      router.replace(target);
    }

    window.sessionStorage?.removeItem(AUTH_LAST_URL_KEY);
    window.sessionStorage?.removeItem(AUTH_REDIRECT_FLAG);
    // Note: router is intentionally omitted from deps - it's stable and including it causes infinite loops in React 19
  }, [status, data?.user?.vaultIds, pathname]);

  return null;
}

export default AuthEffects;
